import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SkillsSection = () => {
  const skills = [
    {
      id: 1,
      name: "MongoDB",
      category: "Database",
      level: 90,
      icon: "Database",
      description: "NoSQL database design and optimization"
    },
    {
      id: 2,
      name: "Express.js",
      category: "Backend",
      level: 95,
      icon: "Server",
      description: "RESTful API development and middleware"
    },
    {
      id: 3,
      name: "React.js",
      category: "Frontend",
      level: 98,
      icon: "Code2",
      description: "Modern React with hooks and context"
    },
    {
      id: 4,
      name: "Node.js",
      category: "Backend",
      level: 92,
      icon: "Zap",
      description: "Server-side JavaScript development"
    },
    {
      id: 5,
      name: "Next.js",
      category: "Frontend",
      level: 75,
      icon: "ArrowRight",
      description: "Full-stack React framework"
    },
    {
      id: 6,
      name: "NestJS",
      category: "Backend",
      level: 60,
      icon: "Shield",
      description: "Scalable Node.js server-side applications"
    },
    {
      id: 7,
      name: "FastAPI",
      category: "Backend",
      level: 55,
      icon: "Rocket",
      description: "High-performance Python web framework"
    },
    {
      id: 8,
      name: "GraphQL",
      category: "Backend",
      level: 70,
      icon: "Rocket",
      description: "GraphQL API development"
    },
    {
      id: 9,
      name: "Docker",
      category: "DevOps",
      level: 75,
      icon: "Package",
      description: "Containerization and orchestration"
    },
    {
      id: 10,
      name: "PostgreSQL",
      category: "Database",
      level: 89,
      icon: "Database",
      description: "Relational database design and queries"
    },
    {
      id: 11,
      name: "Cursor AI",
      category: "AI Tools",
      level: 80,
      icon: "Brain",
      description: "AI coding assistant"
    },
    {
      id: 12,
      name: "Prompt to Code",
      category: "AI Tools",
      level: 80,
      icon: "Brain",
      description: "AI coding assistant"
    },
    {
      id: 13,
      name: "ChatGPT",
      category: "AI Tools",
      level: 80,
      icon: "Brain",
      description: "AI chat assistant"
    }
  ];

  const categories = ["All", "Frontend", "Backend", "Database", "DevOps","AI Tools"];
  const [activeCategory, setActiveCategory] = React.useState("All");

  const filteredSkills = activeCategory === "All" 
    ? skills 
    : skills?.filter(skill => skill?.category === activeCategory);

  return (
    <section id="skills" className="py-20 bg-muted/30">
      <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
            Technical Skills
          </h2>
          <div className="w-20 h-1 bg-primary rounded-full mx-auto"></div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Expertise in modern web technologies and frameworks for building scalable applications
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories?.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                activeCategory === category
                  ? 'bg-primary text-white shadow-elevation-1'
                  : 'bg-background text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Skills Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredSkills?.map((skill, index) => (
            <div
              key={skill?.id}
              className="bg-background rounded-xl p-6 shadow-elevation-1 hover:shadow-elevation-2 transition-all duration-300 group animate-fade-in-stagger"
              style={{ '--stagger-delay': `${index * 100}ms` }}
            >
              {/* Skill Header */}
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-200">
                  <Icon 
                    name={skill?.icon} 
                    size={24} 
                    color="var(--color-primary)" 
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{skill?.name}</h3>
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                    {skill?.category}
                  </span>
                </div>
              </div>

              {/* Skill Description */}
              <p className="text-sm text-muted-foreground mb-4">
                {skill?.description}
              </p>

              {/* Skill Level */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium text-foreground">Proficiency</span>
                  <span className="text-xs font-bold text-primary">{skill?.level}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${skill?.level}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Skills Summary */}
        <div className="mt-16 text-center">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            <div className="space-y-2">
              <div className="text-2xl font-bold text-primary">10+</div>
              <div className="text-sm text-muted-foreground">Technologies</div>
            </div>
            <div className="space-y-2">
              <div className="text-2xl font-bold text-primary">2+</div>
              <div className="text-sm text-muted-foreground">Years Experience</div>
            </div>
            <div className="space-y-2">
              <div className="text-2xl font-bold text-primary">15+</div>
              <div className="text-sm text-muted-foreground">Projects Built</div>
            </div>
            {/* <div className="space-y-2">
              <div className="text-2xl font-bold text-primary">100%</div>
              <div className="text-sm text-muted-foreground">Client Satisfaction</div>
            </div> */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;